import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { ORGANIZATION_TYPE_TRANSLATIONS } from "modules/backoffice/lib/organizations/translations"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeOrganizationsResponseDataSchema,
  BackofficeOrganizationsSortFieldsEnum,
  OrganizationTypeEnum,
} from "schema"
import { z } from "zod"
import { BackofficeImpersonateOrganization } from "./BackofficeImpersonateOrganization"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeOrganizationsResponseDataSchema>[number]
>()

type Props = {
  organizations: z.infer<typeof BackofficeOrganizationsResponseDataSchema> | undefined
  isPending: boolean
  fields: BackofficeOrganizationsSortFieldsEnum[]
  onSort: (field: BackofficeOrganizationsSortFieldsEnum) => void
  search: {
    field: BackofficeOrganizationsSortFieldsEnum
    order: "asc" | "desc"
  }
}

export const BackofficeOrganizationsTable = ({
  organizations,
  isPending,
  fields,
  onSort,
  search,
}: Props) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("id", {
          header: t("backoffice.organizations.table.id"),
        }),
        columnHelper.display({
          id: "companies",
          header: t("backoffice.organizations.table.companies"),
          cell: (props) => (
            <div className="flex flex-col font-mono text-xs text-muted-foreground">
              {props.row.original.companies.map((company) => (
                <span key={company.id}>{company.id}</span>
              ))}
            </div>
          ),
        }),
        columnHelper.accessor("name", {
          header: t("backoffice.organizations.table.name"),
          cell: (props) => {
            return (
              <div className="flex items-center gap-2">
                <img src={props.row.original.logo ?? ""} style={{ height: 30 }} />
                <span>{props.getValue()}</span>
              </div>
            )
          },
          meta: { colSpan: 3 },
        }),
        columnHelper.display({
          header: t("backoffice.organizations.table.credits"),
          cell: (props) => {
            const {
              subscriptions,
              creditPackages,
              packageCredits: remainingCreditPackages,
              subscriptionCredits: remainingSubscriptionCredits,
              type,
            } = props.row.original

            if ([OrganizationTypeEnum.Partner, OrganizationTypeEnum.Integration].includes(type))
              return <span className="font-mono text-xs">{"-"}</span>

            const subscriptionTotalCredits = subscriptions?.length
              ? subscriptions.reduce((acc, curr) => acc + curr.credits, 0)
              : 50
            const totalActivePackageCredits = creditPackages?.length
              ? creditPackages.reduce((acc, curr) => acc + curr.credits, 0)
              : 0

            const totalCredits = subscriptionTotalCredits + totalActivePackageCredits
            const remainingCredits =
              Math.max(remainingSubscriptionCredits, 0) + Math.max(remainingCreditPackages, 0)
            const displayTotalCredits = Math.max(totalCredits, 0)
            const consumedCredits = Math.max(displayTotalCredits - remainingCredits, 0)
            const percentage =
              displayTotalCredits > 0 ? Math.ceil((consumedCredits / displayTotalCredits) * 100) : 0

            return (
              <span className="font-mono text-xs">{`${consumedCredits}/${displayTotalCredits} - (${percentage}%)`}</span>
            )
          },
        }),
        columnHelper.accessor("type", {
          header: t("backoffice.organizations.table.accountType"),
          cell: (props) => {
            const type = props.getValue()

            return (
              <Badge variant="secondary" className="px-2 py-1">
                {t(ORGANIZATION_TYPE_TRANSLATIONS[type])}
              </Badge>
            )
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("backoffice.organizations.table.createdAt"),
          cell: (createdAt) => <DateDisplay date={createdAt.getValue()} />,
          meta: { colSpan: 1, align: "right" },
        }),
        columnHelper.accessor("lastEmployee", {
          id: "lastEmployee",
          header: t("backoffice.organizations.table.lastSeenAt"),
          cell: (props) => {
            const employee = props.getValue()

            if (!employee || !employee.lastSeenAt) {
              return null
            }

            return (
              <Tooltip>
                <TooltipTrigger>
                  <DateDisplay onlyDate date={employee.lastSeenAt} />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-2">
                    <span>{employee.name}</span>
                    <span>{employee.email}</span>
                    <span>{employee.lastSeenAt}</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            )
          },
          meta: { colSpan: 1, align: "right" },
        }),
        columnHelper.display({
          header: t("backoffice.organizations.actions.impersonate"),
          cell: (props) => <BackofficeImpersonateOrganization id={props.row.original.id} />,
          meta: { colSpan: 1, align: "right" },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: organizations ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || organizations === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (organizations.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.organizations.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/backoffice/organizations/$id",
          params: { id: row.original.id },
          search,
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof BackofficeOrganizationsResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as BackofficeOrganizationsSortFieldsEnum)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as BackofficeOrganizationsSortFieldsEnum),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
